import { API_V1 } from '../config'
import { apiFetch } from './api'
import type { CommentAttachment, NegotiationComment } from './negotiationsApi'

// --- API functions for comments ---

export interface PaginatedComments {
  results: NegotiationComment[]
  total_count: number
  has_more: boolean
}

/** List comments visible to the current actor */
export async function listComments(
  negotiationId: number,
  version?: number,
  options?: { threaded?: boolean; limit?: number; offset?: number },
): Promise<NegotiationComment[] | PaginatedComments> {
  const params = new URLSearchParams()
  if (version != null) params.set('version', String(version))
  if (options?.threaded) params.set('threaded', 'true')
  if (options?.limit != null) params.set('limit', String(options.limit))
  if (options?.offset != null) params.set('offset', String(options.offset))

  const query = params.toString()
  const url = `${API_V1}/negotiations/${negotiationId}/comments/${query ? `?${query}` : ''}`
  const response = await apiFetch(url)

  if (!response.ok) {
    throw new Error(`Failed to fetch comments: ${response.status}`)
  }
  return response.json()
}

/** Create a new comment */
export async function createComment(
  negotiationId: number,
  data: {
    content: string
    visibility?: 'INTERNAL' | 'ALL' | 'SPECIFIC'
    target_tag?: string
    parent_comment_id?: number
    anchor_text?: string
    anchor_paragraph?: number
    word_cc_id?: string
    para_id?: string
  },
): Promise<NegotiationComment> {
  const response = await apiFetch(`${API_V1}/negotiations/${negotiationId}/comments/create/`, {
    method: 'POST',
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    const message = errorData?.error || `Failed to create comment: ${response.status}`
    throw new Error(message)
  }
  return response.json()
}

/** Update a comment (content and/or visibility) */
export async function updateComment(
  negotiationId: number,
  commentId: number,
  data: {
    content?: string
    visibility?: 'INTERNAL' | 'ALL' | 'SPECIFIC'
    target_tag?: string
  },
): Promise<NegotiationComment> {
  const response = await apiFetch(
    `${API_V1}/negotiations/${negotiationId}/comments/${commentId}/`,
    {
      method: 'PATCH',
      body: JSON.stringify(data),
    },
  )

  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    const message = errorData?.error || `Failed to update comment: ${response.status}`
    throw new Error(message)
  }
  return response.json()
}

/** Soft-delete a comment */
export async function deleteComment(
  negotiationId: number,
  commentId: number,
): Promise<void> {
  const response = await apiFetch(
    `${API_V1}/negotiations/${negotiationId}/comments/${commentId}/delete/`,
    { method: 'DELETE' },
  )

  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    const message = errorData?.error || `Failed to delete comment: ${response.status}`
    throw new Error(message)
  }
}

/** Resolve or unresolve a comment */
export async function resolveComment(
  negotiationId: number,
  commentId: number,
): Promise<{ resolved: boolean }> {
  const response = await apiFetch(
    `${API_V1}/negotiations/${negotiationId}/comments/${commentId}/resolve/`,
    { method: 'POST' },
  )

  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    const message = errorData?.error || `Failed to resolve comment: ${response.status}`
    throw new Error(message)
  }
  return response.json()
}

/** Batch-classify comments that need classification */
export async function batchClassifyComments(
  negotiationId: number,
  data: {
    comment_ids: number[]
    visibility: 'INTERNAL' | 'ALL' | 'SPECIFIC'
    target_tag?: string
  },
): Promise<{ classified_count: number }> {
  const response = await apiFetch(
    `${API_V1}/negotiations/${negotiationId}/comments/batch-classify/`,
    { method: 'POST', body: JSON.stringify(data) },
  )
  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    throw new Error(errorData?.error || `Failed to classify comments: ${response.status}`)
  }
  return response.json()
}

/** Batch-resolve comments */
export async function batchResolveComments(
  negotiationId: number,
  data: { comment_ids?: number[]; scope?: 'all_mine' | 'thread'; parent_id?: number },
): Promise<{ resolved_count: number; skipped_count: number }> {
  const response = await apiFetch(
    `${API_V1}/negotiations/${negotiationId}/comments/batch-resolve/`,
    { method: 'POST', body: JSON.stringify(data) },
  )
  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    throw new Error(errorData?.error || `Failed to batch-resolve comments: ${response.status}`)
  }
  return response.json()
}

/** Search comments within a negotiation */
export async function searchComments(
  negotiationId: number,
  query: string,
  version?: number,
): Promise<NegotiationComment[]> {
  const params = new URLSearchParams({ q: query, threaded: 'true' })
  if (version != null) params.set('version', String(version))
  const response = await apiFetch(
    `${API_V1}/negotiations/${negotiationId}/comments/search/?${params}`,
  )
  if (!response.ok) throw new Error(`Failed to search comments: ${response.status}`)
  return response.json()
}

/** Add an attachment to a comment */
export async function addAttachment(
  negotiationId: number,
  commentId: number,
  file: File,
): Promise<CommentAttachment> {
  const formData = new FormData()
  formData.append('file', file)

  const response = await apiFetch(
    `${API_V1}/negotiations/${negotiationId}/comments/${commentId}/attachments/`,
    { method: 'POST', body: formData, headers: {} },
  )
  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    throw new Error(errorData?.error || `Failed to upload attachment: ${response.status}`)
  }
  return response.json()
}

/** Delete an attachment */
export async function deleteAttachment(
  negotiationId: number,
  commentId: number,
  attachmentId: number,
): Promise<void> {
  const response = await apiFetch(
    `${API_V1}/negotiations/${negotiationId}/comments/${commentId}/attachments/${attachmentId}/`,
    { method: 'DELETE' },
  )
  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    throw new Error(errorData?.error || `Failed to delete attachment: ${response.status}`)
  }
}

/** Download an attachment */
export function getAttachmentDownloadUrl(
  negotiationId: number,
  commentId: number,
  attachmentId: number,
): string {
  return `${API_V1}/negotiations/${negotiationId}/comments/${commentId}/attachments/${attachmentId}/download/`
}

// --- Reactions ---

/** Add a reaction to a comment */
export async function addReaction(
  negotiationId: number,
  commentId: number,
  emoji: string,
): Promise<{ emoji: string; created: boolean }> {
  const response = await apiFetch(
    `${API_V1}/negotiations/${negotiationId}/comments/${commentId}/reactions/`,
    { method: 'POST', body: JSON.stringify({ emoji }) },
  )
  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    throw new Error(errorData?.error || `Failed to add reaction: ${response.status}`)
  }
  return response.json()
}

/** Remove a reaction from a comment */
export async function removeReaction(
  negotiationId: number,
  commentId: number,
  emoji: string,
): Promise<{ deleted: boolean }> {
  const response = await apiFetch(
    `${API_V1}/negotiations/${negotiationId}/comments/${commentId}/reactions/remove/`,
    { method: 'DELETE', body: JSON.stringify({ emoji }) },
  )
  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    throw new Error(errorData?.error || `Failed to remove reaction: ${response.status}`)
  }
  return response.json()
}

// --- Typing Indicator ---

/** Send typing indicator to notify other users */
export async function sendTypingIndicator(
  negotiationId: number,
): Promise<{ ok: boolean }> {
  const response = await apiFetch(
    `${API_V1}/negotiations/${negotiationId}/typing/`,
    { method: 'POST' },
  )
  if (!response.ok) {
    // Typing indicator is best-effort, don't throw
    return { ok: false }
  }
  return response.json()
}
